import { useMemo } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { useFormContext, useWatch } from 'react-hook-form';

import { filterParameters, NewParameter, type ParameterFilterFormValues } from '@blocks';
import { BaseNode, Category } from '@data';
import { useTranslate } from '@i18n';
import {
    P_POSITION_REGEX,
    activeSwingIdAtom,
    activeUserIdAtom,
    activityIdAtom,
    swingFoundationCategoriesByPositionAtom,
    swingFoundationGroupedParametersAtom,
} from '@store';
import { Box, Button, Heading3, Inline, LegoIcon, Stack, u } from '@ui';

import {
    clearManyParameterIdsAtom,
    displaySelectedParameterIds,
    selectManyParameterIdsAtom,
    selectParameterIdAtom,
} from './atoms';
import * as css from './PositionGroup.css';

type NewPositionGroupProps = BaseNode;

export const PositionGroup = ({ i18n, id }: NewPositionGroupProps) => {
    const { control } = useFormContext<ParameterFilterFormValues>();
    const filters = useWatch({ control, name: 'filters' });
    const positions = useWatch({ control, name: 'positions' });
    const activityId = useAtomValue(activityIdAtom);
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);

    const selectedParameterIds = useAtomValue(displaySelectedParameterIds);

    const categoriesByPosition = useAtomValue(swingFoundationCategoriesByPositionAtom);
    const position = id.match(P_POSITION_REGEX)?.[0] ?? '';
    const categories: Category[] | undefined = categoriesByPosition[position];

    const groupedParameters = useAtomValue(swingFoundationGroupedParametersAtom);
    const positionGroupedParameters = groupedParameters[position] ?? {};
    const t = useTranslate();

    const visibleParametersByCategory = useMemo(
        () =>
            Object.fromEntries(
                (categories ?? []).map(({ id: categoryId }) => [
                    categoryId,
                    filterParameters(positionGroupedParameters[categoryId] ?? [], filters, positions),
                ]),
            ),
        [categories, filters, positionGroupedParameters, positions],
    );
    const filteredCategories = useMemo(
        () => (categories ?? []).filter(({ id: categoryId }) => visibleParametersByCategory[categoryId]?.length > 0),
        [categories, visibleParametersByCategory],
    );
    const visibleParameters = Object.values(visibleParametersByCategory).flat();

    const selectedParametersWithinGroup = useMemo(
        () => visibleParameters.filter((param) => selectedParameterIds?.includes(param.id)),
        [visibleParameters, selectedParameterIds],
    );

    const canClearAll = !!selectedParametersWithinGroup.length;
    const canSelectAll = selectedParametersWithinGroup.length < visibleParameters.length;

    const handleSelectParameterId = useSetAtom(selectParameterIdAtom);
    const handleSelectAllParameterIds = useSetAtom(selectManyParameterIdsAtom);
    const handleClearAllParameterIds = useSetAtom(clearManyParameterIdsAtom);

    if (!position) return null;
    if (visibleParameters.length === 0) return null;

    return (
        <Stack gap={u(4)} p={u(2.5)} className={css.position}>
            <Box display="flex" columnGap={u(4)}>
                <Inline alignItems="center">
                    <LegoIcon />
                    <Heading3>{t(i18n ?? '-')}</Heading3>
                </Inline>
                <Inline>
                    <Button
                        variant="secondary"
                        size="small"
                        isDisabled={!canClearAll}
                        onClick={() => handleClearAllParameterIds(visibleParameters.map(({ id }) => id))}
                    >
                        {t('swing_foundations_editor.clear_all_button')}
                    </Button>
                    <Button
                        variant="primary"
                        size="small"
                        isDisabled={!canSelectAll}
                        onClick={() => handleSelectAllParameterIds(visibleParameters.map(({ id }) => id))}
                    >
                        {t('swing_foundations_editor.select_all_button')}
                    </Button>
                </Inline>
            </Box>
            <Stack flexWrap="nowrap" gap={u(4)}>
                {filteredCategories.map((group) => {
                    return (
                        <Stack gap={u(2)} flexWrap="nowrap" key={group?.id || 'other'}>
                            <Heading3>{t(group?.i18n ?? 'Other')}</Heading3>
                            <Inline gap={u(2)} flexWrap="wrap">
                                {visibleParametersByCategory[group.id]?.map((parameter) => {
                                    return (
                                        <NewParameter
                                            key={parameter.id}
                                            node={parameter}
                                            isSelected={selectedParameterIds?.includes(parameter.id)}
                                            onClick={() => handleSelectParameterId(parameter.id)}
                                            activityId={activityId}
                                            activeSwingId={activeSwingId}
                                            activeUserId={activeUserId}
                                        />
                                    );
                                })}
                            </Inline>
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
};
